'use client';

import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { CheckIcon, MinusIcon } from '@phosphor-icons/react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const checkboxVariants = cva(
    'grid place-content-center peer h-5 w-5 shrink-0 rounded-[4px] box-border border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grey-400 focus-visible:ring-offset-2 focus-visible:ring-offset-grey-900 disabled:cursor-not-allowed',
    {
        variants: {
            variant: {
                default:
                    'border-grey-200 bg-grey-600 hover:bg-grey-500 hover:border-grey-100 data-[state=checked]:bg-white-100 data-[state=checked]:border-white-100 data-[state=indeterminate]:bg-white-100 data-[state=indeterminate]:border-white-100 disabled:border-grey-600 disabled:bg-transparent disabled:hover:border-grey-600 disabled:hover:bg-transparent',
                white: 'border-grey-200 bg-transparent hover:bg-grey-650 hover:border-grey-200 data-[state=checked]:bg-white-100 data-[state=checked]:border-white-100 data-[state=indeterminate]:bg-white-100 data-[state=indeterminate]:border-white-100 disabled:border-grey-600 disabled:bg-transparent disabled:hover:border-grey-600 disabled:hover:bg-transparent',
            },
        },
        defaultVariants: {
            variant: 'default',
        },
    },
);

type CheckboxRootProps = React.ComponentPropsWithoutRef<
    typeof CheckboxPrimitive.Root
> &
    VariantProps<typeof checkboxVariants>;

const Checkbox = React.forwardRef<
    React.ElementRef<typeof CheckboxPrimitive.Root>,
    CheckboxRootProps
>(({ className, variant = 'default', ...props }, ref) => (
    <CheckboxPrimitive.Root
        ref={ref}
        className={cn('group', checkboxVariants({ variant }), className)}
        {...props}
    >
        <CheckboxPrimitive.Indicator className="grid place-content-center text-grey-700 [&_svg]:shrink-0">
            <span className="relative flex h-3 w-3 items-center justify-center">
                <CheckIcon
                    size={12}
                    weight="bold"
                    className="absolute group-data-[state=indeterminate]:hidden"
                />
                <MinusIcon
                    size={12}
                    weight="bold"
                    className="absolute group-data-[state=checked]:hidden"
                />
            </span>
        </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox, checkboxVariants };
